import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  ProjectActivitiesResponse,
  ProjectActivityUserRow,
  ProjectActivityUsersResponse,
  ProjectEoiAgentRow,
} from '../../services/api';
import {
  DateRangeSelector,
  DateRangeState,
  DateRangePreset,
  formatInputDate,
  parseInputDate,
  isBeforeDate,
  calendarToday,
  DEFAULT_DATE_RANGE_PRESETS,
} from '../admin/DateRangeSelector';

interface ProjectActivitiesTabProps {
  /** Per-agent drill-in is Admin-only; bars are non-interactive for other roles. */
  isAdmin?: boolean;
}

const CHART_COLORS = [
  '#ED8936', '#4299E1', '#38B2AC', '#ECC94B', '#9F7AEA',
  '#F56565', '#48BB78', '#ED64A6', '#667EEA', '#F6AD55',
];

/** Activities data is backfilled from 1 Mar 2026; "All time" snaps to that lower bound. */
const ALL_TIME_START = '2026-03-01';

/** Max number of projects drawn as separate lines in the breakdown view. */
const MAX_BREAKDOWN_SERIES = 8;
/** Max number of bars shown in the by-project chart. */
const MAX_BARS = 25;

function createAllTimeRange(): DateRangeState {
  const today = calendarToday();
  const start = parseInputDate(ALL_TIME_START);
  const end = start && isBeforeDate(today, start) ? start : today;
  return { startDate: ALL_TIME_START, endDate: formatInputDate(end) };
}

const DATE_PRESETS: DateRangePreset[] = [
  ...DEFAULT_DATE_RANGE_PRESETS,
  { id: 'allTime', label: 'All time', build: () => createAllTimeRange() },
];

const chartCardSx = {
  p: 2.5,
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  height: '100%',
} as const;

const formatCount = (value: number): string =>
  Number.isFinite(value) ? value.toLocaleString() : '-';

/** Shorten long project names for the bar-chart Y axis. */
const truncate = (name: string, max = 26): string =>
  name.length > max ? `${name.slice(0, max)}…` : name;

type ChartTooltipProps = {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ value?: number | string | null; color?: string; name?: string }>;
};

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, label, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const visible = payload.filter((p) => Number(p?.value || 0) > 0);
  if (visible.length === 0) {
    return null;
  }
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(20, 28, 43, 0.96)',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
        maxWidth: 280,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}>
        {String(label ?? '-')}
      </Typography>
      <Stack spacing={0.5}>
        {visible.map((entry, index) => (
          <Box
            key={`${entry?.name || 'value'}-${index}`}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Typography variant="caption" sx={{ color: entry?.color || 'rgba(255,255,255,0.72)' }}>
              {entry?.name || 'Activities'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: entry?.color || '#63B3ED' }}>
              {formatCount(Number(entry?.value || 0))}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const StatCard: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <Paper elevation={0} sx={{ ...chartCardSx, p: 2 }}>
    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: accent || 'var(--color-text-strong)', mt: 0.5 }}>
      {value}
    </Typography>
  </Paper>
);

type DrillSortField = 'ceaNumber' | 'name' | 'mobile' | 'viewsAllTime' | 'viewsPeriod' | 'soldAllTime' | 'soldPeriod';
type SortDir = 'asc' | 'desc';

const stickyHeadCellSx = {
  backgroundColor: 'var(--color-surface-thead, #141c2b)',
  color: 'var(--color-text-strong)',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  zIndex: 3,
} as const;

type EoiSortField = 'ceaNumber' | 'name' | 'mobile' | 'viewsPeriod' | 'eoiCount';

const sortHeadSx = {
  color: 'inherit !important',
  '& .MuiTableSortLabel-icon': { color: 'var(--color-accent) !important' },
} as const;

interface ProjectDrillContentProps {
  projectName: string;
  rows: ProjectActivityUserRow[];
  soldRateLimited: boolean;
}

/**
 * Drill-in body for a single project (Admin only). Mirrors the Analytics Dashboard's
 * project drill-down: an "All Users" tab and an "EOI" tab, a shared "Remove Converted
 * Agent" toggle, and a shared CEA/name/mobile filter.
 */
const ProjectDrillContent: React.FC<ProjectDrillContentProps> = ({ projectName, rows, soldRateLimited }) => {
  const [tab, setTab] = useState<'all' | 'eoi'>('all');
  const [filter, setFilter] = useState('');
  const [removeConverted, setRemoveConverted] = useState(false);

  const [sortField, setSortField] = useState<DrillSortField>('viewsPeriod');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [eoiSortField, setEoiSortField] = useState<EoiSortField>('eoiCount');
  const [eoiSortDir, setEoiSortDir] = useState<SortDir>('desc');

  const [convertedSet, setConvertedSet] = useState<Set<string>>(new Set());
  const [convertedLoading, setConvertedLoading] = useState(true);
  const [eois, setEois] = useState<ProjectEoiAgentRow[]>([]);
  const [eoisLoaded, setEoisLoaded] = useState(false);
  const [eoisLoading, setEoisLoading] = useState(false);
  const [eoiSending, setEoiSending] = useState<Record<string, boolean>>({});
  const [eoiSendError, setEoiSendError] = useState('');

  // Converted agents = anyone with a converted transaction (all-time), matching the dashboard.
  // Loaded eagerly when the drill opens; the "Remove Converted Agent" toggle stays disabled
  // until this resolves so it never silently no-ops on a half-loaded set.
  useEffect(() => {
    let cancelled = false;
    setConvertedLoading(true);
    const allTime = createAllTimeRange();
    api.getAdminByDateAnalytics({ startDate: allTime.startDate, endDate: allTime.endDate })
      .then((res) => {
        if (cancelled) return;
        const set = new Set<string>();
        for (const r of res.rows) {
          if ((r.convertedTransactionCount || 0) > 0) {
            set.add(r.ceaNumber.trim().toUpperCase());
          }
        }
        setConvertedSet(set);
      })
      .catch(() => { if (!cancelled) setConvertedSet(new Set()); })
      .finally(() => { if (!cancelled) setConvertedLoading(false); });
    return () => { cancelled = true; };
  }, [projectName]);

  // EOIs load lazily the first time the EOI tab is opened.
  useEffect(() => {
    if (tab !== 'eoi' || eoisLoaded) return;
    let cancelled = false;
    setEoisLoading(true);
    api.getAdminProjectEois(projectName)
      .then((res) => { if (!cancelled) { setEois(res.agents); setEoisLoaded(true); } })
      .catch(() => { if (!cancelled) setEois([]); })
      .finally(() => { if (!cancelled) setEoisLoading(false); });
    return () => { cancelled = true; };
  }, [tab, eoisLoaded, projectName]);

  const handleSort = (field: DrillSortField) => {
    setSortDir((prev) => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setSortField(field);
  };
  const handleEoiSort = (field: EoiSortField) => {
    setEoiSortDir((prev) => (eoiSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setEoiSortField(field);
  };

  const q = filter.trim().toLowerCase();
  const matchesFilter = (r: { ceaNumber: string; name?: string; mobile?: string }) =>
    !q ||
    r.ceaNumber.toLowerCase().includes(q) ||
    (r.name || '').toLowerCase().includes(q) ||
    (r.mobile || '').toLowerCase().includes(q);
  const isConverted = (cea: string) => convertedSet.has(cea.trim().toUpperCase());

  const filteredUsers = useMemo(() => {
    let list = rows.filter(matchesFilter);
    if (removeConverted) list = list.filter((r) => !isConverted(r.ceaNumber));
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'ceaNumber': cmp = a.ceaNumber.localeCompare(b.ceaNumber); break;
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break;
        case 'mobile': cmp = (a.mobile || '').localeCompare(b.mobile || ''); break;
        case 'viewsAllTime': cmp = a.viewsAllTime - b.viewsAllTime; break;
        case 'viewsPeriod': cmp = a.viewsPeriod - b.viewsPeriod; break;
        case 'soldAllTime': cmp = a.soldAllTime - b.soldAllTime; break;
        case 'soldPeriod': cmp = a.soldPeriod - b.soldPeriod; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q, removeConverted, convertedSet, sortField, sortDir]);

  const viewsPeriodByCea = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) map.set(r.ceaNumber, r.viewsPeriod || 0);
    return map;
  }, [rows]);

  const filteredEois = useMemo(() => {
    let list = eois
      .map((r) => ({ ...r, viewsPeriod: viewsPeriodByCea.get(r.ceaNumber) ?? 0 }))
      .filter(matchesFilter);
    if (removeConverted) list = list.filter((r) => !isConverted(r.ceaNumber));
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (eoiSortField) {
        case 'ceaNumber': cmp = a.ceaNumber.localeCompare(b.ceaNumber); break;
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break;
        case 'mobile': cmp = (a.mobile || '').localeCompare(b.mobile || ''); break;
        case 'viewsPeriod': cmp = a.viewsPeriod - b.viewsPeriod; break;
        case 'eoiCount': cmp = a.eoiCount - b.eoiCount; break;
      }
      return eoiSortDir === 'asc' ? cmp : -cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eois, viewsPeriodByCea, q, removeConverted, convertedSet, eoiSortField, eoiSortDir]);

  const sendWhatsApp = async (ceaNumber: string) => {
    setEoiSendError('');
    setEoiSending((prev) => ({ ...prev, [ceaNumber]: true }));
    try {
      const res = await api.sendAdminProjectEoiWhatsApp(projectName, ceaNumber);
      setEois((prev) => prev.map((r) => (r.ceaNumber === ceaNumber ? { ...r, engagedCount: res.engagedCount } : r)));
    } catch (err: any) {
      setEoiSendError(err?.message || 'Failed to send WhatsApp');
    } finally {
      setEoiSending((prev) => { const next = { ...prev }; delete next[ceaNumber]; return next; });
    }
  };

  const numericHead = (field: DrillSortField, label: string) => (
    <TableCell align="right" sx={stickyHeadCellSx}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
        sx={sortHeadSx}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const textHead = (field: DrillSortField, label: string) => (
    <TableCell sx={stickyHeadCellSx}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
        sx={sortHeadSx}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const eoiHead = (field: EoiSortField, label: string, align: 'left' | 'right') => (
    <TableCell align={align} sx={stickyHeadCellSx}>
      <TableSortLabel
        active={eoiSortField === field}
        direction={eoiSortField === field ? eoiSortDir : 'asc'}
        onClick={() => handleEoiSort(field)}
        sx={sortHeadSx}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const renderMobile = (mobile?: string) =>
    mobile?.trim() ? (
      <a href={`tel:${mobile}`} style={{ color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
        {mobile}
      </a>
    ) : '—';

  return (
    <Stack spacing={2}>
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          '& .MuiTab-root': { textTransform: 'none', color: 'rgba(var(--color-text-rgb), 0.65)' },
          '& .Mui-selected': { color: 'var(--color-text-strong) !important' },
        }}
      >
        <Tab value="all" label={`All Users (${filteredUsers.length})`} />
        <Tab value="eoi" label={`EOI (${eoisLoaded ? filteredEois.length : eois.length})`} />
      </Tabs>

      <Stack spacing={1}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={removeConverted}
              disabled={convertedLoading}
              onChange={(e) => setRemoveConverted(e.target.checked)}
              sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
            />
          }
          label={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2">Remove Converted Agent</Typography>
              {convertedLoading && <CircularProgress size={12} sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }} />}
            </Box>
          }
        />
        <TextField
          size="small"
          fullWidth
          placeholder="Filter by CEA, name, or mobile…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {tab === 'eoi' && eoiSendError && (
        <Alert severity="error" onClose={() => setEoiSendError('')}>{eoiSendError}</Alert>
      )}

      {tab === 'all' ? (
        <TableContainer sx={{ maxHeight: 'min(58vh, 620px)', overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {textHead('ceaNumber', 'CEA Number')}
                {textHead('name', 'Name')}
                <TableCell sx={stickyHeadCellSx}>Mobile</TableCell>
                {numericHead('viewsAllTime', 'Views (all time)')}
                {numericHead('viewsPeriod', 'Views (selected period)')}
                {numericHead('soldAllTime', 'Sold (all time)')}
                {numericHead('soldPeriod', 'Sold (selected period)')}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    {filter || removeConverted ? 'No results matching your filter.' : 'No agent data for this project.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((r) => (
                  <TableRow key={r.ceaNumber} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.ceaNumber}</TableCell>
                    <TableCell>{r.name || '—'}</TableCell>
                    <TableCell>{renderMobile(r.mobile)}</TableCell>
                    <TableCell align="right">{formatCount(r.viewsAllTime)}</TableCell>
                    <TableCell align="right">{formatCount(r.viewsPeriod)}</TableCell>
                    <TableCell align="right">
                      {soldRateLimited ? (
                        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          (API limit, retry in 1 min)
                        </Typography>
                      ) : formatCount(r.soldAllTime)}
                    </TableCell>
                    <TableCell align="right">
                      {soldRateLimited ? (
                        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          (API limit, retry in 1 min)
                        </Typography>
                      ) : formatCount(r.soldPeriod)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : eoisLoading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 'min(58vh, 620px)', overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {eoiHead('ceaNumber', 'CEA Number', 'left')}
                {eoiHead('name', 'Name', 'left')}
                <TableCell sx={stickyHeadCellSx}>Mobile</TableCell>
                {eoiHead('viewsPeriod', 'Views', 'right')}
                {eoiHead('eoiCount', 'No. EOIs', 'right')}
                <TableCell align="center" sx={stickyHeadCellSx}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEois.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    {filter || removeConverted ? 'No results matching your filter.' : 'No EOIs registered for this project.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEois.map((r) => {
                  const sending = !!eoiSending[r.ceaNumber];
                  const disabled = sending || !r.mobile?.trim();
                  return (
                    <TableRow key={r.ceaNumber} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.ceaNumber}</TableCell>
                      <TableCell>{r.name || '—'}</TableCell>
                      <TableCell>{renderMobile(r.mobile)}</TableCell>
                      <TableCell align="right">{formatCount(r.viewsPeriod)}</TableCell>
                      <TableCell align="right">{formatCount(r.eoiCount)}</TableCell>
                      <TableCell align="center">
                        <Stack alignItems="center" spacing={0.5}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={disabled}
                            onClick={() => void sendWhatsApp(r.ceaNumber)}
                            startIcon={sending ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <WhatsAppIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              textTransform: 'none',
                              bgcolor: '#25D366',
                              color: '#fff',
                              '&:hover': { bgcolor: '#1ebe57' },
                              '&.Mui-disabled': { bgcolor: 'rgba(37, 211, 102, 0.35)', color: '#fff' },
                              minWidth: 110,
                            }}
                          >
                            {sending ? 'Sending…' : 'WhatsApp'}
                          </Button>
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Engaged {r.engagedCount} time{r.engagedCount === 1 ? '' : 's'}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

/**
 * "Project Activities" — engagement activity across the projects the user leads
 * (all projects for Admin / Project Dept). The first tab of Project Head →
 * Statistics; it used to be its own sidebar entry.
 */
const ProjectActivitiesTab: React.FC<ProjectActivitiesTabProps> = ({ isAdmin = false }) => {
  const muiTheme = useMuiTheme();
  const [range, setRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [data, setData] = useState<ProjectActivitiesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  // An IC running a project with no Head is in Project Head, but this view is
  // for Heads / Deputy Heads; the server says so with NO_LED_PROJECTS.
  const [notLead, setNotLead] = useState(false);
  const [search, setSearch] = useState('');
  const [breakdown, setBreakdown] = useState(false);

  // Drill-in (per-agent breakdown for a single project)
  const [drillProject, setDrillProject] = useState<string | null>(null);
  const [drillData, setDrillData] = useState<ProjectActivityUsersResponse | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState('');

  const chartGridStroke = muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(31,41,68,0.10)';
  const chartTick = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 12 }),
    [muiTheme.palette.text.secondary]
  );
  const chartTickSmall = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 11 }),
    [muiTheme.palette.text.secondary]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotLead(false);
    try {
      const response = await api.getProjectActivities({
        startDate: range.startDate,
        endDate: range.endDate,
      });
      setData(response);
    } catch (err: any) {
      if (err?.code === 'NO_LED_PROJECTS') setNotLead(true);
      else setError(err?.message || 'Failed to load project activities');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range.startDate, range.endDate]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const openDrill = useCallback(
    async (projectName: string) => {
      setDrillProject(projectName);
      setDrillData(null);
      setDrillError('');
      setDrillLoading(true);
      try {
        const response = await api.getProjectActivityUsers({
          projectName,
          startDate: range.startDate,
          endDate: range.endDate,
        });
        setDrillData(response);
      } catch (err: any) {
        setDrillError(err?.message || 'Failed to load agent breakdown');
      } finally {
        setDrillLoading(false);
      }
    },
    [range.startDate, range.endDate]
  );

  const closeDrill = useCallback(() => {
    setDrillProject(null);
    setDrillData(null);
    setDrillError('');
  }, []);

  const normalizedSearch = search.trim().toLowerCase();

  // Project totals after search filter, sorted desc (already sorted from API).
  const filteredTotals = useMemo(() => {
    const totals = data?.projectTotals ?? [];
    if (!normalizedSearch) {
      return totals;
    }
    return totals.filter((t) => t.projectName.toLowerCase().includes(normalizedSearch));
  }, [data, normalizedSearch]);

  const visibleProjectNames = useMemo(
    () => new Set(filteredTotals.map((t) => t.projectName)),
    [filteredTotals]
  );

  // Bar chart data (capped for readability).
  const barData = useMemo(
    () => filteredTotals.slice(0, MAX_BARS).map((t) => ({
      projectName: t.projectName,
      label: truncate(t.projectName),
      activities: t.activities,
    })),
    [filteredTotals]
  );

  // The projects that get their own line in breakdown mode (top N by total).
  const breakdownProjects = useMemo(
    () => filteredTotals.slice(0, MAX_BREAKDOWN_SERIES).map((t) => t.projectName),
    [filteredTotals]
  );

  // Time series. Each row keyed by date; aggregate total + optional per-project values.
  const lineData = useMemo(() => {
    const series = (data?.series ?? []).filter((p) => visibleProjectNames.has(p.projectName));
    const byDate = new Map<string, Record<string, number>>();
    const breakdownSet = new Set(breakdownProjects);
    for (const point of series) {
      const row = byDate.get(point.date) || { __total: 0 };
      row.__total += point.activities;
      if (breakdownSet.has(point.projectName)) {
        row[point.projectName] = (row[point.projectName] || 0) + point.activities;
      }
      byDate.set(point.date, row);
    }
    return Array.from(byDate.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, values]) => ({ date, total: values.__total, ...values }));
  }, [data, visibleProjectNames, breakdownProjects]);

  const totalActivities = useMemo(
    () => filteredTotals.reduce((sum, t) => sum + t.activities, 0),
    [filteredTotals]
  );

  const avgPerDay = lineData.length > 0 ? Math.round(totalActivities / lineData.length) : 0;

  const scopeLabel = data?.scope === 'all' ? 'All projects' : 'My projects';

  const hasData = filteredTotals.length > 0;

  if (notLead) {
    return (
      <Alert severity="info">
        Project Activities covers the projects you lead as Head or Deputy Head of Project. You are not
        either on any active project.
      </Alert>
    );
  }

  return (
    <>
    <Box>
        {/* Filters */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 2.5 }}
        >
          <DateRangeSelector range={range} onApply={setRange} presets={DATE_PRESETS} />
          <TextField
            size="small"
            placeholder="Search project name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ flex: 1 }} />
          {data && (
            <Chip
              size="small"
              label={scopeLabel}
              sx={{
                alignSelf: 'center',
                bgcolor: 'rgba(var(--color-accent-rgb, 237,137,54), 0.15)',
                color: 'var(--color-accent)',
                fontWeight: 600,
              }}
            />
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void fetchData()}
            disabled={loading}
            sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {data?.warnings?.length ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {data.warnings.join(' · ')}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : !hasData ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
              No activity found for this date range
              {normalizedSearch ? ' and search' : ''}.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary stats */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={6} md={3}>
                <StatCard label="Total Activities" value={formatCount(totalActivities)} accent="var(--color-accent)" />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard label="Projects" value={formatCount(filteredTotals.length)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard label="Active Days" value={formatCount(lineData.length)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard label="Avg / Active Day" value={formatCount(avgPerDay)} />
              </Grid>
            </Grid>

            <Grid container spacing={2.5}>
              {/* Bar chart: total activities per project */}
              <Grid item xs={12} lg={5}>
                <Paper elevation={0} sx={chartCardSx}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Activities by Project
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                    {isAdmin ? 'Click a bar to view the agent breakdown' : 'Total activity per project'}
                    {filteredTotals.length > MAX_BARS ? ` · showing top ${MAX_BARS} of ${filteredTotals.length}` : ''}
                  </Typography>
                  <Box sx={{ height: Math.max(320, barData.length * 34), mt: 1.5 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={barData}
                        layout="vertical"
                        margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                        <XAxis type="number" tick={chartTickSmall} stroke={chartGridStroke} />
                        <YAxis
                          type="category"
                          dataKey="label"
                          width={170}
                          tick={chartTickSmall}
                          stroke={chartGridStroke}
                          interval={0}
                        />
                        <RechartsTooltip
                          content={<ChartTooltip />}
                          cursor={{ fill: 'rgba(var(--color-text-rgb), 0.06)' }}
                        />
                        <Bar
                          dataKey="activities"
                          name="Activities"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={26}
                          cursor={isAdmin ? 'pointer' : 'default'}
                          onClick={isAdmin ? (d: any) => {
                            const name = d?.projectName ?? d?.payload?.projectName;
                            if (name) void openDrill(name);
                          } : undefined}
                        >
                          {barData.map((entry, index) => (
                            <Cell key={entry.projectName} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              {/* Line chart: activities over time */}
              <Grid item xs={12} lg={7}>
                <Paper elevation={0} sx={chartCardSx}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Activities Over Time
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                        {breakdown
                          ? `Daily activity per project${filteredTotals.length > MAX_BREAKDOWN_SERIES ? ` · top ${MAX_BREAKDOWN_SERIES}` : ''}`
                          : 'Total daily activity'}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={breakdown}
                          onChange={(e) => setBreakdown(e.target.checked)}
                          sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                        />
                      }
                      label={<Typography variant="body2">Break down by project</Typography>}
                    />
                  </Box>
                  <Box sx={{ height: Math.max(320, barData.length * 34), mt: 1.5 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={lineData} margin={{ top: 8, right: 24, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                        <XAxis dataKey="date" tick={chartTickSmall} stroke={chartGridStroke} minTickGap={24} />
                        <YAxis tick={chartTick} stroke={chartGridStroke} allowDecimals={false} />
                        <RechartsTooltip content={<ChartTooltip />} />
                        {breakdown ? (
                          breakdownProjects.map((projectName, index) => (
                            <Line
                              key={projectName}
                              type="monotone"
                              dataKey={projectName}
                              name={truncate(projectName, 22)}
                              stroke={CHART_COLORS[index % CHART_COLORS.length]}
                              strokeWidth={2}
                              dot={false}
                              connectNulls
                            />
                          ))
                        ) : (
                          <Line
                            type="monotone"
                            dataKey="total"
                            name="Activities"
                            stroke="#ED8936"
                            strokeWidth={2.5}
                            dot={false}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
    </Box>

    {/* Drill-in: per-agent breakdown for a single project */}
    <Dialog
      open={!!drillProject}
      onClose={closeDrill}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '60vh',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{drillProject}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              Agent activity · {range.startDate} → {range.endDate}
            </Typography>
          </Box>
          <IconButton onClick={closeDrill} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {drillError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDrillError('')}>
            {drillError}
          </Alert>
        )}
        {drillLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : drillData && drillProject ? (
          <ProjectDrillContent
            key={drillProject}
            projectName={drillProject}
            rows={drillData.topUsers}
            soldRateLimited={drillData.soldCountsRateLimited}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={closeDrill}>Close</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ProjectActivitiesTab;
